import { Inject, Injectable } from '@angular/core';
import { deepCopy, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwQuery, DwQueryFilter } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { AppMetaData, CheckRideRequestEntity, CheckrideRequestHomePageQueryEntity, CheckRideRequestSubStatusAllItems } from '../../../meta-data/app-meta-data.service';
import * as moment_ from 'moment';

const moment = moment_;

export type HomeCheckrideScope = 'examiner' | 'school' | 'applicant';

/** Dedicated checkride loader for the new home pages; classic dashboards keep their existing queries. */
@Injectable({ providedIn: 'root' })
export class HomeCheckrideRepositoryService {
  constructor(
    @Inject(DwMetaDataServiceToken) private metadata: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private orm: DwOrmDataService
  ) { }

  loadSchoolTaskCheckrides(ownerId: number): Observable<CheckRideRequestEntity[]> {
    const query = AppMetaData.CheckRideRequest.CreateQueryBuilder();
    query.query.FieldSettings.IncludeRootItemFields = false;
    query.query.FieldSettings.LoadAllLookupDisplayFields = false;
    query.query.FieldSettings.LoadCustomAttributes = false;
    query.addFields(a => [a.CheckRideRequestId, a.ScheduledDateTime, a.CheckRideRequestSubStatusId, a.CheckRideWorkflowId]);
    query.addFilterAnd(f => f.Equal(a => a.FlightSchoolOrganizationId, ownerId || -1));
    query.addFilterAnd(f => f.Equal(a => a.CheckRideOwnershipTypeId, 2));
    query.addFilterAnd(f => f.NotEqual(a => a.CheckRideRequestSubStatusId, CheckRideRequestSubStatusAllItems.Deleted));
    const today = moment().startOf('day');
    // Allow a day either side of the display window for CAP's local-time conversion.
    query.addFilterAnd(f => f.GroupOr(
      g => g.GroupAnd(
        a => this.exactDateTimeFilter(a.GreaterThanEqual(x => x.ScheduledDateTime, today.clone().subtract(1, 'day').format('YYYY-MM-DD'))),
        a => this.exactDateTimeFilter(a.LessThan(x => x.ScheduledDateTime, today.clone().add(9, 'days').format('YYYY-MM-DD')))),
      g => g.InList(a => a.CheckRideRequestSubStatusId, [
        CheckRideRequestSubStatusAllItems.PendingApproval, CheckRideRequestSubStatusAllItems.PendingApprovalPayment,
        CheckRideRequestSubStatusAllItems.AdditionalInformationNeeded, CheckRideRequestSubStatusAllItems.RequiresAdditionalInfo,
        CheckRideRequestSubStatusAllItems.PendingOutcome, CheckRideRequestSubStatusAllItems.NewDateProposedByExaminer,
        CheckRideRequestSubStatusAllItems.PendingReschedule])
    ));
    return this.orm.executeQuery<CheckRideRequestEntity>(query);
  }

  load(scope: HomeCheckrideScope, ownerId: number, checkrideIds?: number[]): Observable<CheckrideRequestHomePageQueryEntity[]> {
    if (checkrideIds && !checkrideIds.length) {
      return of([]);
    }
    return AppMetaData.Queries.CheckrideRequestHomePage.CreateQueryBuilder(this.metadata).pipe(take(1), mergeMap(saved => {
      // Saved metadata may be cached: never mutate its query with a user's scope.
      const copy = Object.assign(new DwQuery(saved.query.RootTable), deepCopy(saved.query));
      const query = AppMetaData.Queries.CheckrideRequestHomePage.CreateQueryBuilderFromQuery(copy);
      query.addFilterAnd(f => f.Equal(a => scope === 'examiner' ? a.ScheduledPilotExaminerId
        : scope === 'school' ? a.FlightSchoolOrganizationId : a.StudentApplicantId, ownerId || -1));
      query.addFilterAnd(f => f.NotEqual(a => a.CheckRideRequestSubStatusId, CheckRideRequestSubStatusAllItems.Deleted));
      if (scope === 'school') {
        query.addFilterAnd(f => f.Equal(a => a.CheckRideOwnershipTypeId, 2));
      }
      if (checkrideIds) {
        query.addFilterAnd(f => f.InList(a => a.CheckRideRequestId, checkrideIds));
        query.query.FieldSettings.IncludeRootItemFields = false;
        // CAP generates lookup display values; *IdDisplay names are not SQL columns.
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;
        query.query.FieldSettings.LoadCustomAttributes = false;
        query.query.FieldSettings.IncludeFields = [];
        query.query.RelationFields = [];
        query.addFields(a => [
          a.CheckRideRequestId, a.ScheduledDateTime, a.ScheduledTimeMinutes, a.CheckRideRequestSubStatusId,
          a.CheckRideTypeId, a.CheckRideTestTypeId, a.CheckRideGroupId, a.ScheduledAirportId,
          a.AirplaneMakeModel, a.AirplaneNNumber, a.AuthorityApplicationNumber, a.ScheduledCheckRideCost,
          a.PilexosBookingCost, a.PilexosBookingCostPaymentPartyId, a.PilexosBookingPaymentAdded,
          a.AirportAirportCode, a.AirportAirportName, a.RoCheckrideApplicantInfoApplicantFinalName,
          a.RoCheckrideApplicantInfoFlightSchoolFinalName, a.RoCheckrideApplicantInfoPilotExaminerFinalName,
          a.RoCheckrideApplicantInfoFlightInstructorFinalName
        ]);
      }
      if (scope !== 'applicant' && !checkrideIds) {
        // Keep SQL date literals stable for the day so repeated loads can reuse a plan.
        const today = moment().startOf('day');
        const startDate = today.clone().subtract(1, 'day').format('YYYY-MM-DD');
        const endDateExclusive = today.clone().add(15, 'days').format('YYYY-MM-DD');
        query.addFilterAnd(f => f.GroupOr(
          g => g.GroupAnd(
            a => this.exactDateTimeFilter(a.GreaterThanEqual(x => x.ScheduledDateTime, startDate)),
            a => this.exactDateTimeFilter(a.LessThan(x => x.ScheduledDateTime, endDateExclusive))),
          g => g.InList(a => a.CheckRideRequestSubStatusId, [
            CheckRideRequestSubStatusAllItems.PendingApproval, CheckRideRequestSubStatusAllItems.PendingApprovalPayment,
            CheckRideRequestSubStatusAllItems.PendingOutcome, CheckRideRequestSubStatusAllItems.NewDateProposedByExaminer,
            CheckRideRequestSubStatusAllItems.PendingPayment, CheckRideRequestSubStatusAllItems.PendingReschedule,
            ...(scope === 'school' ? [CheckRideRequestSubStatusAllItems.Cancelled] : [])])
        ));
      }
      // Only these two relationships are required. Do not load full POA or written-test trees.
      query.query.Prefetch = [];
      const poa = AppMetaData.PlanOfAction.CreateQueryBuilder();
      poa.query.FieldSettings.IncludeRootItemFields = false;
      poa.query.FieldSettings.LoadAllLookupDisplayFields = false;
      poa.query.FieldSettings.LoadCustomAttributes = false;
      poa.addFields(a => [a.PlanOfActionId]);
      query.addPrefetch(r => r.PlanOfAction, poa.query);
      const written = AppMetaData.ApplicantAcsWrittenResult.CreateQueryBuilder();
      written.query.FieldSettings.IncludeRootItemFields = false;
      written.query.FieldSettings.LoadAllLookupDisplayFields = false;
      written.query.FieldSettings.LoadCustomAttributes = false;
      written.addFields(a => [a.AcsTestId]);
      query.addPrefetch(r => r.ApplicantAcsWrittenResult, written.query);
      return this.orm.executeQuery<CheckrideRequestHomePageQueryEntity>(query);
    }));
  }

  private exactDateTimeFilter(filter: DwQueryFilter): DwQueryFilter {
    // Compare the column directly to midnight; avoid CAP's date-only column conversion.
    filter.ExactDateTimeCompare = true;
    return filter;
  }
}
